import { forwardRef, useEffect, useId, useRef, useState } from "react";
import type { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes } from "react";
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

// ── AuthCard ────────────────────────────────────────────────────────────────

interface AuthCardProps {
  title: string;
  subtitle?: ReactNode;
  eyebrow?: string;
  badge?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthCard({ title, subtitle, eyebrow, badge, children, footer }: AuthCardProps) {
  return (
    <div className="relative">
      {/* Subtle border-glow */}
      <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-primary/30 via-transparent to-violet-500/20 opacity-50 blur-md pointer-events-none" />
      <div className="relative rounded-3xl border border-border/40 bg-card/70 backdrop-blur-xl shadow-2xl shadow-black/30 p-6 sm:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            {eyebrow && (
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary/85">
                {eyebrow}
              </span>
            )}
            {badge}
          </div>
          <h1 className="text-2xl sm:text-[28px] font-black tracking-tight leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{subtitle}</p>
          )}
        </div>
        {children}
        {footer && <div className="mt-7">{footer}</div>}
      </div>
    </div>
  );
}

// ── AuthInput ───────────────────────────────────────────────────────────────

interface AuthInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon?: ReactNode;
  trailing?: ReactNode;
  hint?: ReactNode;
  validationState?: "idle" | "valid" | "invalid";
  error?: string;
}

export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(function AuthInput(
  { label, icon, trailing, hint, validationState = "idle", error, className, id, ...rest },
  ref,
) {
  const fallbackId = useId();
  const fieldId = id ?? fallbackId;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={fieldId}
          className="text-[11px] font-semibold uppercase tracking-[0.15em] text-foreground/80"
        >
          {label}
        </label>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      <div
        className={cn(
          "group relative flex items-center rounded-xl border bg-background/50 backdrop-blur-sm transition-all",
          "focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/15",
          validationState === "valid"
            ? "border-emerald-500/40"
            : validationState === "invalid" || error
              ? "border-rose-500/40"
              : "border-border/60",
        )}
      >
        {icon && (
          <span className="pl-3 text-muted-foreground flex-shrink-0 group-focus-within:text-primary transition-colors">
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={fieldId}
          {...rest}
          className={cn(
            "flex-1 bg-transparent outline-none px-3 py-2.5 text-sm placeholder:text-muted-foreground/50",
            "disabled:cursor-not-allowed disabled:opacity-60",
            className,
          )}
        />
        {validationState === "valid" && (
          <CheckCircle2 className="h-4 w-4 text-emerald-400 mr-3 flex-shrink-0" />
        )}
        {trailing && <span className="pr-2 flex-shrink-0">{trailing}</span>}
      </div>
      {error && (
        <p className="text-[11px] text-rose-400 flex items-center gap-1.5">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
});

// ── PasswordField ────────────────────────────────────────────────────────────

export interface PasswordFieldProps extends Omit<AuthInputProps, "type" | "trailing"> {
  showStrength?: boolean;
  matchValue?: string; // if set, shows match/mismatch indicator
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ showStrength, matchValue, value, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const innerRef = useRef<HTMLInputElement | null>(null);

    // Combine refs
    const combinedRef = (el: HTMLInputElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const ev = e.nativeEvent as KeyboardEvent;
      if (typeof ev.getModifierState === "function") {
        setCapsLock(ev.getModifierState("CapsLock"));
      }
    };

    const password = String(value ?? "");
    const strength = showStrength ? scorePassword(password) : null;
    const matches =
      matchValue !== undefined && password.length > 0
        ? password === matchValue
        : null;

    return (
      <div className="space-y-2">
        <AuthInput
          ref={combinedRef}
          {...rest}
          type={visible ? "text" : "password"}
          value={password}
          onKeyDown={(e) => {
            onKey(e);
            rest.onKeyDown?.(e);
          }}
          onKeyUp={(e) => {
            onKey(e);
            rest.onKeyUp?.(e);
          }}
          validationState={
            matches === true
              ? "valid"
              : matches === false
                ? "invalid"
                : rest.validationState
          }
          trailing={
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              tabIndex={-1}
              aria-label={visible ? "Hide password" : "Show password"}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
            >
              {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
        />

        {capsLock && (
          <p className="text-[11px] text-amber-400 flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" />
            Caps Lock is on
          </p>
        )}

        {showStrength && password.length > 0 && strength && (
          <div className="pt-1">
            <div className="flex gap-1.5 mb-1">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1 flex-1 rounded-full transition-colors",
                    i < strength.score ? strength.barClass : "bg-border/60",
                  )}
                />
              ))}
            </div>
            <div className="flex items-center justify-between text-[10px]">
              <span className={cn("font-bold uppercase tracking-wider", strength.textClass)}>
                {strength.label}
              </span>
              <span className="text-muted-foreground">{strength.hint}</span>
            </div>
          </div>
        )}

        {matches === false && password.length > 0 && (
          <p className="text-[11px] text-rose-400 flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" />
            Passwords don't match yet.
          </p>
        )}
      </div>
    );
  },
);

// ── AuthButton ──────────────────────────────────────────────────────────────

interface AuthButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  success?: boolean;
  variant?: "primary" | "ghost";
}

export function AuthButton({
  children,
  loading,
  success,
  variant = "primary",
  className,
  disabled,
  ...rest
}: AuthButtonProps) {
  const base =
    "relative w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold tracking-tight transition-all";
  const variants: Record<string, string> = {
    primary:
      "bg-gradient-to-r from-primary via-primary to-primary/85 text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-primary/35 hover:-translate-y-px",
    ghost:
      "border border-border/50 bg-background/40 backdrop-blur-sm text-foreground hover:bg-background/70",
  };
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={cn(base, variants[variant], "disabled:opacity-60 disabled:cursor-not-allowed", className)}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Working...
        </>
      ) : success ? (
        <>
          <CheckCircle2 className="h-4 w-4" />
          Done
        </>
      ) : (
        children
      )}
    </button>
  );
}

// ── Password strength scorer ────────────────────────────────────────────────

function scorePassword(pw: string): {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  hint: string;
  barClass: string;
  textClass: string;
} {
  if (pw.length === 0) {
    return { score: 0, label: "Empty", hint: "", barClass: "bg-border/60", textClass: "text-muted-foreground" };
  }
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  const variety = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].reduce(
    (n, r) => n + (r.test(pw) ? 1 : 0),
    0,
  );
  if (variety >= 3) score += 1;
  if (variety === 4 && pw.length >= 12) score += 1;

  // Penalize obvious sequences
  if (/(.)\1{2,}/.test(pw)) score = Math.max(0, score - 1);
  if (/123|abc|qwer|password/i.test(pw)) score = Math.max(0, score - 1);

  const map = [
    { label: "Too short", hint: "Use at least 8 characters", bar: "bg-rose-500", text: "text-rose-400" },
    { label: "Weak", hint: "Add letters, numbers, symbols", bar: "bg-rose-400", text: "text-rose-400" },
    { label: "Fair", hint: "Mix in another character type", bar: "bg-amber-400", text: "text-amber-400" },
    { label: "Strong", hint: "Solid password", bar: "bg-emerald-400", text: "text-emerald-400" },
    { label: "Excellent", hint: "Great mix and length", bar: "bg-emerald-300", text: "text-emerald-300" },
  ];
  const meta = map[Math.min(4, score)];
  return {
    score: Math.min(4, score) as 0 | 1 | 2 | 3 | 4,
    label: meta.label,
    hint: meta.hint,
    barClass: meta.bar,
    textClass: meta.text,
  };
}

// ── AuthDivider ─────────────────────────────────────────────────────────────

export function AuthDivider({ children = "or" }: { children?: ReactNode }) {
  return (
    <div className="my-5 flex items-center gap-3">
      <div className="flex-1 h-px bg-border/60" />
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {children}
      </span>
      <div className="flex-1 h-px bg-border/60" />
    </div>
  );
}
